import { useState, useEffect } from "react";
import { Button, Modal, Table } from "@mantine/core";
import { Plus } from "tabler-icons-react";
import { useSearchParams } from "react-router-dom";

import { VehicleGroup } from "@captiva/shared-types";
import { VehicleGroupAssetConfig } from "../../components/listings/configurations/assets/vehicle-group-asset-config";
import { AssetEditor } from "../../components/shared/asset-editor";
import { FlexOptionsChoiceModal } from "./shared/flex-options-choice-modal";
import { useCreateFlexOptionsSet } from "../../hooks/useCreateFlexOptionsSet";
import { bulkGenerateAndDownloadQR } from "../../components/shared/bulk-qr-generator";
import TableRowActions from "./shared/table-row-actions";
import { BulkActionsMenu } from "./shared/bulk-actions-menu";

export interface AuthUser {
  uid: string;
  email?: string;
  claims?: {
    companyId?: string;
  };
}

interface VehicleGroupsTableProps {
  elements: VehicleGroup[];
  auth: AuthUser | null;
  reloadCallback: () => void;
}

export function VehicleGroupsTable({ elements, auth, reloadCallback }: VehicleGroupsTableProps) {
  const newGroup: Partial<VehicleGroup> = {
    name: "",
    description: "",
  };
  const [searchParams, setSearchParams] = useSearchParams();
  const [editedElement, setEditedElement] = useState<VehicleGroup | Partial<VehicleGroup> | undefined>(undefined);

  useEffect(() => {
    const editId = searchParams.get('editId');
    if (editId) {
      const match = elements.find((el) => el.id === editId);
      if (match) {
        setEditedElement(match);
      }
    }
  }, [searchParams, elements]);

  const closeEdit = () => {
    setEditedElement(undefined);
    if (searchParams.get('editId')) {
      searchParams.delete('editId');
      setSearchParams(searchParams, { replace: true });
    }
  };
  const [createFlexOptionsSet, { loading, notification, clearNotification, existingDocs, mergeOptions, createNewSet }] = useCreateFlexOptionsSet();
  
  const handleCreateFlexOptionsSet = () => {
    const companyId = auth?.claims?.companyId;
    if (!companyId) {
      alert("No company ID found in auth claims.");
      return;
    }
    createFlexOptionsSet({
      assetType: "Vehicle Groups",
      assetList: elements,
      metadataPrefix: "vehicle-group",
      fieldMappings: {
        label: "name",
        value: "id",
        metadata: {
          name: "name",
        }
      },
      companyId
    });
  };
  
  async function handleBulkQRDownload() {
    await bulkGenerateAndDownloadQR({
      assetName: "Vehicle Group",
      items: elements,
      nameKey: "name",
      idKey: "id",
      zipName: "vehicle-groups-qr-codes.zip",
      logoSrc: "/captiva-mark.png"
    });
  }

  const rows = elements.map((element) => (
    <tr key={element.id}>
      <td>{element.name}</td>
      <td>{element.description}</td>
      <td>
        <TableRowActions
          element={element}
          setEditedElement={setEditedElement}
          reloadCallback={reloadCallback}
          assetName="Vehicle Group"
          collectionName="fleetGroups"
          nameKey="name"
          idKey="id"
        />
      </td>
    </tr>
  ));

  return (
    <>
      {notification && notification.type !== "choice" && (
        <div style={{ position: "fixed", top: 76, right: 20, zIndex: 9999 }}>
          <Button color={notification.type === "success" ? "green" : "red"} variant="light" onClick={clearNotification}>
            {notification.message}
          </Button>
        </div>
      )}
      {elements.length > 0 && (
        <Table width="100%">
          <thead>
            <tr>
              <th>Name</th>
              <th>Description</th>
              <th style={{textAlign: 'left'}}>
                <BulkActionsMenu onBulkQRDownload={handleBulkQRDownload} onCreateFlexOptionsSet={handleCreateFlexOptionsSet} loading={loading} />
              </th>
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </Table>
      )}
      {editedElement && (
        <Modal opened onClose={closeEdit} title="Vehicle group">
          <AssetEditor
            asset={editedElement}
            assetConfig={VehicleGroupAssetConfig}
            collection="fleetGroups"
            auth={auth}
            reloadCallback={() => { reloadCallback(); closeEdit(); }}
          ></AssetEditor>
        </Modal>
      )}
      <Button onClick={() => setEditedElement(newGroup)} fullWidth my="xl" color="cap-navy" variant="outline" leftIcon={<Plus />}>
        Create new
      </Button>
      {notification && notification.type === "choice" && (
        <FlexOptionsChoiceModal
          opened={true}
          onClose={() => clearNotification()}
          onMerge={mergeOptions}
          onCreateNew={createNewSet}
          existingDocs={existingDocs}
          assetType="Vehicle Groups"
        />
      )}
    </>
  );
}
